var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Grid, Stack, Typography, styled } from '@mui/material';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PlanType } from '../../enum';
import { Autocomplete, FormField, LineBarChart, StyledDatePicker } from '../common';
import { lineBarLayout } from '../common/chart/config';
import { useReportPlanType } from '../report/selectors';
import { ALL_LOCATIONS_ID, DAILY_FILTERS, DATE_PICKER_FORMAT, NORMAL_CHART, WORK_VOLUME_CHART } from './constants';
import { CHART_KEYS, DAILY_FILTER_KEYS, FREQUENCY_ENUM, METRICS_ENUM } from './enum';
import { createParamsFetchActual, formatDayOfWeek, formattedDate, getSummaryChart, useActualReportAPI, useReportSummaryData, useReportTimelineOptions, } from './selectors';
var StyledBoxLegendGroup = styled(Box)(function (_a) {
    var theme = _a.theme;
    return ({
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    });
});
var StyledBoxLegendItem = styled(Box)(function (_a) {
    var _b, _c, _d, _e;
    var item = _a.item;
    return (__assign({ width: '1.9375rem' }, ((item === null || item === void 0 ? void 0 : item.type) === 'bar'
        ? { height: '1.0625rem', backgroundColor: String((_c = (_b = item === null || item === void 0 ? void 0 : item.marker) === null || _b === void 0 ? void 0 : _b.color) !== null && _c !== void 0 ? _c : 'inherit') }
        : { height: '0.125rem', backgroundColor: String((_e = (_d = item === null || item === void 0 ? void 0 : item.line) === null || _d === void 0 ? void 0 : _d.color) !== null && _e !== void 0 ? _e : 'inherit') })));
});
var StyledTypographyLegend = styled(Typography)(function (_a) {
    var _b, _c, _d, _e;
    var theme = _a.theme, item = _a.item;
    return ({
        fontWeight: 'bold',
        fontSize: theme.typography.pxToRem(16),
        color: ((_b = item === null || item === void 0 ? void 0 : item.line) === null || _b === void 0 ? void 0 : _b.color) ? String((_c = item === null || item === void 0 ? void 0 : item.line) === null || _c === void 0 ? void 0 : _c.color) : String((_e = (_d = item === null || item === void 0 ? void 0 : item.marker) === null || _d === void 0 ? void 0 : _d.color) !== null && _e !== void 0 ? _e : 'inherit'),
    });
});
var StyledStackLegend = styled(Stack)(function (_a) {
    var theme = _a.theme;
    return ({
        position: 'absolute',
        bottom: '1rem',
        width: '100%',
        flexDirection: 'row',
        gap: theme.spacing(10),
        alignItems: 'center',
        justifyContent: 'center',
        marginY: theme.spacing(2),
    });
});
var StyledTitleChart = styled(Typography)(function (_a) {
    var theme = _a.theme;
    return ({
        fontWeight: 'bold',
        fontSize: theme.typography.pxToRem(18),
    });
});
var StyledSummaryChartContainer = styled(Box)(function () { return ({
    position: 'relative',
}); });
var StyledStackFilters = styled(Stack)(function (_a) {
    var theme = _a.theme;
    return ({
        padding: theme.spacing(0, 4),
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(6),
        minHeight: '2.5rem',
        flexWrap: 'wrap',
    });
});
var StyledGridFilters = styled(Grid)(function (_a) {
    var theme = _a.theme;
    return ({
        display: 'flex',
        gap: theme.spacing(4),
        flexWrap: 'wrap',
        alignItems: 'center',
    });
});
export var DaiLyFilters = function () {
    var _a = useFormContext(), control = _a.control, watch = _a.watch, setValue = _a.setValue, handleSubmit = _a.handleSubmit;
    var t = useTranslation('translation', { keyPrefix: 'dateFormat' }).t;
    var tFilter = useTranslation('report', { keyPrefix: 'actual.dailyFilter' }).t;
    var _b = watch(), dailyViewType = _b.dailyViewType, periodStart = _b.periodStart, periodEnd = _b.periodEnd, specificDay = _b.specificDay;
    var timelines = useReportTimelineOptions();
    var planType = useReportPlanType();
    var _c = useActualReportAPI(), fetchActualReport = _c[1], clearActualReport = _c[2];
    var handleFetchActualReport = handleSubmit(function (formData) {
        clearActualReport();
        fetchActualReport({}, createParamsFetchActual(formData));
    });
    var onChangeDate = function (date) {
        setValue('specificDay', date);
        void handleFetchActualReport();
    };
    var startLimitDate = useMemo(function () {
        var _a;
        var startDateSelected = (_a = timelines === null || timelines === void 0 ? void 0 : timelines.find(function (item) { return item.id === periodStart; })) === null || _a === void 0 ? void 0 : _a.moment;
        return moment(startDateSelected);
    }, [periodStart, timelines]);
    var endLimitDate = useMemo(function () {
        var _a;
        var endDateByPlan = planType === PlanType.Weekly ? periodEnd : periodStart;
        var endDateSelected = (_a = timelines === null || timelines === void 0 ? void 0 : timelines.find(function (item) { return item.id === endDateByPlan; })) === null || _a === void 0 ? void 0 : _a.moment;
        if (planType === PlanType.Weekly) {
            return moment(endDateSelected).add(6, 'day');
        }
        return moment(endDateSelected).add(1, 'month').subtract(1, 'day');
    }, [planType, periodStart, periodEnd, timelines]);
    useEffect(function () {
        var _a, _b;
        if (dailyViewType === DAILY_FILTER_KEYS.SPECIFIC_DAY) {
            var startMoment = (_a = timelines === null || timelines === void 0 ? void 0 : timelines.find(function (item) { return item.id === periodStart; })) === null || _a === void 0 ? void 0 : _a.moment;
            var endMoment = (_b = timelines === null || timelines === void 0 ? void 0 : timelines.find(function (item) { return item.id === periodEnd; })) === null || _b === void 0 ? void 0 : _b.moment;
            if (planType === PlanType.Weekly) {
                endMoment = moment(endMoment).add(6, 'day');
            }
            else {
                endMoment = moment(startMoment).add(1, 'month').subtract(1, 'day');
            }
            if (!specificDay || specificDay.isBefore(startMoment) || specificDay.isAfter(endMoment)) {
                setValue('specificDay', moment(startMoment));
            }
        }
        else {
            setValue('specificDay', null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dailyViewType, periodStart, periodEnd, timelines, setValue, t]);
    return (_jsxs(StyledGridFilters, { children: [_jsx(FormField, __assign({ required: true, name: "dailyViewType", control: control }, { children: _jsx(Autocomplete, { sx: { minWidth: '13rem' }, disablePortal: true, options: DAILY_FILTERS.map(function (item) { return ({ value: item, name: tFilter(item) }); }), getOptionValue: function (o) { return o.value; }, onChange: handleFetchActualReport }) })), dailyViewType === DAILY_FILTER_KEYS.SPECIFIC_DAY && (_jsx(FormField, __assign({ required: true, name: "specificDay", control: control }, { children: _jsx(StyledDatePicker, { minDate: startLimitDate, maxDate: endLimitDate, control: control, format: DATE_PICKER_FORMAT, onChange: onChangeDate }) })))] }));
};
export var LegendChart = function (_a) {
    var data = _a.data;
    var t = useTranslation('report', { keyPrefix: 'actual.summaryChart.legend' }).t;
    return (_jsx(StyledStackLegend, { children: data.map(function (item) { return (_jsxs(StyledBoxLegendGroup, { children: [_jsx(StyledBoxLegendItem, { item: item }), _jsx(StyledTypographyLegend, __assign({ item: item }, { children: t(item.key) }))] }, item.key)); }) }));
};
export var SummaryChart = function (_a) {
    var isFilter = _a.isFilter, title = _a.title, data = _a.data, layout = _a.layout, xAxisConfig = _a.xAxisConfig;
    var watch = useFormContext().watch;
    var frequency = watch().frequency;
    return (_jsxs(StyledSummaryChartContainer, { children: [_jsxs(StyledStackFilters, { children: [_jsx(StyledTitleChart, { children: title }), frequency === FREQUENCY_ENUM.DAILY && isFilter && _jsx(DaiLyFilters, {})] }), _jsx(LineBarChart, { data: data, layout: layout, config: { displayModeBar: false, doubleClick: false }, xAxisConfig: xAxisConfig }), _jsx(LegendChart, { data: data })] }));
};
export var DataPanelSummaryChart = function () {
    var t = useTranslation('report', { keyPrefix: 'actual.summaryChart' }).t;
    var watch = useFormContext().watch;
    var _a = watch(), metricTab = _a.metricTab, frequency = _a.frequency, dailyViewType = _a.dailyViewType, periodStart = _a.periodStart, periodEnd = _a.periodEnd, actualAttritionMode = _a.actualAttritionMode, queueLocationId = _a.queueLocationId;
    var isLocationScope = queueLocationId !== undefined && queueLocationId !== ALL_LOCATIONS_ID;
    var timelines = useReportTimelineOptions();
    var _b = useReportSummaryData(), chart = _b.chart, details = _b.details;
    var chartData = useMemo(function () {
        var _a;
        var labels = (_a = timelines === null || timelines === void 0 ? void 0 : timelines.map(function (item) { return item.chart; }).slice(periodStart, periodEnd + 1)) !== null && _a !== void 0 ? _a : [];
        if (frequency === FREQUENCY_ENUM.DAILY) {
            if (details && typeof details === 'object') {
                var detailKeys = Object.keys(details);
                labels =
                    detailKeys.length > 0
                        ? detailKeys.map(function (key) {
                            return dailyViewType === DAILY_FILTER_KEYS.DAILY_AVERAGE
                                ? "".concat(formatDayOfWeek(formattedDate(key)), "<br>").concat(formattedDate(key))
                                : key;
                        })
                        : [];
            }
            else {
                labels = [];
            }
        }
        var layout = cloneDeep(lineBarLayout);
        var buildControls = { metricTab: metricTab, frequency: frequency, dailyViewType: dailyViewType, labels: labels, layout: layout, chart: chart, actualAttritionMode: actualAttritionMode };
        var normalChart = getSummaryChart(t('planVsActual'), __assign(__assign({}, buildControls), { sampleData: NORMAL_CHART }));
        if (metricTab === METRICS_ENUM.WORK_OFFERED_HANDLED) {
            var workVolumeSampleData = isLocationScope
                ? WORK_VOLUME_CHART.filter(function (item) { return item.key !== CHART_KEYS.ABANDONMENT_RATE; })
                : WORK_VOLUME_CHART;
            var workVolumeData = getSummaryChart(t('offeredVsHandled'), __assign(__assign({}, buildControls), { sampleData: workVolumeSampleData }));
            return [__assign(__assign({}, normalChart), { isFilter: true }), workVolumeData];
        }
        return [__assign(__assign({}, normalChart), { isFilter: true })];
    }, [
        timelines,
        periodStart,
        periodEnd,
        frequency,
        metricTab,
        dailyViewType,
        chart,
        actualAttritionMode,
        t,
        details,
        isLocationScope,
    ]);
    return (_jsx(Stack, { children: chartData.map(function (item, index) { return (_jsx(SummaryChart, __assign({}, item), metricTab + index)); }) }));
};
